/**
 * Who is signed in, and what they are allowed to do.
 *
 * WHY THIS IS NOT KEPT IN REACT QUERY
 * -----------------------------------
 * Query cache is for data the server owns and that goes stale. This is the
 * answer to "should this screen even render", which every route and half the
 * buttons ask on every render. It needs to be synchronous and always present.
 *
 * WHERE THE TRUTH ACTUALLY LIVES
 * ------------------------------
 * On the server. Everything here is a copy used to decide what to show, and
 * hiding a button is a courtesy, not a control — the backend checks the same
 * permission again on every request. A tampered copy changes what a screen
 * looks like and nothing else.
 */
import { create } from 'zustand';
import { api, tokens, setSessionEndedHandler } from '../lib/api.js';

const USER_KEY = 'foodrun.user';

/**
 * The signed-in user is remembered alongside the token.
 *
 * Without it, every reload shows a spinner while /me answers — on a till that
 * is a blank screen between taps. The stored copy renders immediately and is
 * replaced by the server's answer a moment later.
 */
function rememberedUser() {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function remember(user) {
  try {
    if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
    else localStorage.removeItem(USER_KEY);
  } catch {
    /* private mode — the session lasts until the tab closes */
  }
}

export const useAuth = create((set, get) => ({
  user: rememberedUser(),
  /** True until the remembered session has been checked against the server. */
  checking: Boolean(tokens.access),
  error: null,

  /** Signed in as far as this device knows. Confirmed once `checking` is false. */
  get isSignedIn() {
    return Boolean(get().user && tokens.access);
  },

  async signIn(email, password) {
    set({ error: null });

    try {
      const data = await api.post('/auth/login', { email, password });
      tokens.set(data);
      remember(data.user);
      set({ user: data.user, checking: false, error: null });

      // Roles arrive with the login, permissions do not. Fetching the rest in
      // the background means the screen appears now and fills in a moment
      // later, rather than the person waiting on two calls to sign in.
      get().refreshProfile();

      return data.user;
    } catch (error) {
      set({ error: error.message });
      throw error;
    }
  },

  async signOut() {
    // Tell the server so the session record is closed, but never block on it:
    // signing out must work when the branch machine is unreachable, which is
    // exactly when someone is trying to hand over to the next shift.
    api.post('/auth/logout').catch(() => {});

    tokens.clear();
    remember(null);
    set({ user: null, checking: false, error: null });
  },

  /** Ask the server who this is. Used on startup and after signing in. */
  async refreshProfile() {
    if (!tokens.access) {
      set({ user: null, checking: false });
      return null;
    }

    try {
      const user = await api.get('/auth/me');
      remember(user);
      set({ user, checking: false });
      return user;
    } catch (error) {
      // A rejected session is over. Anything else — the branch machine being
      // off, for instance — leaves the remembered user in place, because the
      // person is still signed in and the app still works from cache.
      if (error.isAuth) {
        tokens.clear();
        remember(null);
        set({ user: null, checking: false });
      } else {
        set({ checking: false });
      }
      return null;
    }
  },

  /** Does this person have a specific permission? */
  can(permission) {
    const { user } = get();
    if (!user) return false;

    // The owner's role is defined as having everything, so new features do not
    // need a migration to become visible to them.
    if (user.roles?.includes('super_admin')) return true;

    return user.permissions?.includes(permission) ?? false;
  },

  /** Does this person hold any of these roles? */
  hasRole(...roles) {
    const held = get().user?.roles ?? [];
    return roles.some((role) => held.includes(role));
  },
}));

// When a refresh fails deep inside a request, the store hears about it here
// rather than every caller having to handle it.
setSessionEndedHandler(() => {
  remember(null);
  useAuth.setState({ user: null, checking: false });
});
